import { ArrowRight, LogIn, Layers, Link2, Upload, CheckCircle2, Package, Send, Download, MonitorSmartphone, UserX } from 'lucide-react';

/**
 * Dual-Pipeline Flowchart — Magnidrop.
 *
 * Two stacked horizontal flow sequences (Receive Raw Footage / Deliver Final Cut).
 * Clean minimalist typography + sleek sunset-orange connector arrows.
 */

const RECEIVE_STEPS = [
    { icon: LogIn, title: 'Editor Login', sub: 'Sign in to your studio dashboard' },
    { icon: Layers, title: 'Spin Up a Portal', sub: 'One-click new intake portal' },
    { icon: Link2, title: 'Send Secure Link', sub: 'Auto-generated portal URL' },
    { icon: Upload, title: 'Client Drops Files', sub: 'No signup required' },
    { icon: CheckCircle2, title: 'Transfer Completes', sub: 'Instant landing in your files tab' },
];

const DELIVER_STEPS = [
    { icon: Package, title: 'Upload Final Cut', sub: 'Rendered assets → portal files tab' },
    { icon: Send, title: 'Share Delivery Link', sub: 'One-click client delivery URL' },
    { icon: Download, title: 'Client Reviews & Downloads', sub: 'Streams the final output' },
    { icon: MonitorSmartphone, title: 'Multi-Device Downloads', sub: 'Zero cap on downloads' },
    { icon: UserX, title: 'No Client Account', sub: 'Frictionless delivery, always' },
];

function FlowStep({ icon: Icon, title, sub, index }) {
    return (
        <div
            data-testid={`flow-step-${index}`}
            className="flex-1 min-w-[9.5rem] flex flex-col items-start gap-2 group"
        >
            <div className="h-10 w-10 grid place-items-center border border-border bg-card group-hover:border-[#FF6B35]/60 transition-colors">
                <Icon className="h-4 w-4 text-[#FF6B35]" strokeWidth={2} />
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Step {index + 1}
            </p>
            <p className="text-sm font-medium leading-tight">{title}</p>
            <p className="text-xs text-muted-foreground leading-snug">{sub}</p>
        </div>
    );
}

function FlowRow({ title, tag, steps, tagColor = '#FF6B35' }) {
    return (
        <div className="border border-border bg-card/60 backdrop-blur-sm p-6 lg:p-8">
            <div className="flex items-baseline gap-3 mb-6">
                <span
                    className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 border"
                    style={{ borderColor: `${tagColor}66`, color: tagColor }}
                >
                    {tag}
                </span>
                <h3 className="font-heading text-lg sm:text-xl font-medium tracking-tight">{title}</h3>
            </div>
            <div className="flex flex-wrap lg:flex-nowrap items-start gap-4 lg:gap-2">
                {steps.map((step, i) => (
                    <div key={step.title} className="flex items-start lg:items-center gap-4 lg:gap-2 flex-1 min-w-[9.5rem]">
                        <FlowStep {...step} index={i} />
                        {i < steps.length - 1 && (
                            <ArrowRight
                                className="hidden lg:block h-4 w-4 text-[#FF6B35] shrink-0 mt-8"
                                strokeWidth={2}
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function DualPipelineFlow() {
    return (
        <section className="border-b border-border py-20" data-testid="dual-pipeline-flow">
            <div className="mx-auto max-w-7xl px-6">
                <div className="max-w-3xl mb-10">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">The Magnidrop pipeline</p>
                    <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-medium tracking-tighter">
                        Two routes. Zero friction. One portal URL.
                    </h2>
                </div>
                <div className="space-y-6">
                    <FlowRow
                        tag="Route 01"
                        title="Receiving Raw Footage from Client"
                        steps={RECEIVE_STEPS}
                    />
                    <FlowRow
                        tag="Route 02"
                        title="Deliver the Final Cut"
                        steps={DELIVER_STEPS}
                    />
                </div>
            </div>
        </section>
    );
}
